import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Stack,
  Typography,
  Box,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import { GitFork, ScanSearch } from 'lucide-react';
import { type AiEngine, AI_ENGINE_OPTIONS } from './ScanEngineDialog';

interface RepoInfo {
  repoSlug: string;
  repoName: string;
  repoUrl?: string;
  category?: string;
  extractedAt?: string;
}

interface RepoSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  repos: RepoInfo[];
  onConfirm: (selectedRepos: string[], agentMode: AiEngine) => void;
  isLoading?: boolean;
  defaultEngine?: AiEngine;
}

export default function RepoSelectionDialog({
  open,
  onClose,
  repos,
  onConfirm,
  isLoading = false,
  defaultEngine = 'claude',
}: RepoSelectionDialogProps) {
  const theme = useTheme();
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());
  const [selectedEngine, setSelectedEngine] = useState<AiEngine>(defaultEngine);

  const filteredRepos = repos;

  // Group repos by category
  const reposByCategory = filteredRepos.reduce<Record<string, RepoInfo[]>>((acc, repo) => {
    const cat = repo.category || 'uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(repo);
    return acc;
  }, {});

  // Initialize with no repos selected when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedRepos(new Set());
      setSelectedEngine(defaultEngine);
    }
  }, [open, defaultEngine]);

  const handleToggleRepo = (repoSlug: string) => {
    setSelectedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(repoSlug)) {
        next.delete(repoSlug);
      } else {
        next.add(repoSlug);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedRepos(new Set(filteredRepos.map((r) => r.repoSlug)));
  };

  const handleDeselectAll = () => {
    setSelectedRepos(new Set());
  };

  const allSelected = selectedRepos.size === filteredRepos.length && filteredRepos.length > 0;
  const noneSelected = selectedRepos.size === 0;

  const handleConfirm = () => {
    onConfirm(Array.from(selectedRepos), selectedEngine);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GitFork size={20} />
          <Typography variant="h6" fontWeight={600}>
            Select Repositories to Scan
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose which repositories to include in this extraction
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {/* AI Engine selector */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
            AI Engine
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            {AI_ENGINE_OPTIONS.map((opt) => {
              const isSelected = selectedEngine === opt.value;
              const isDefault = opt.value === defaultEngine;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setSelectedEngine(opt.value)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                      borderColor: 'primary.light',
                      bgcolor: alpha(theme.palette.primary.main, isSelected ? 0.08 : 0.03),
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? 'primary.main' : 'text.secondary' }}>
                    {opt.label}
                  </Typography>
                  {isDefault && (
                    <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: alpha(theme.palette.text.secondary, 0.08), fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.6 }}>
                      default
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant={allSelected ? 'contained' : 'outlined'}
            onClick={handleSelectAll}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDeselectAll}
            disabled={noneSelected}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Deselect All
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {selectedRepos.size} of {filteredRepos.length} selected
          </Typography>
        </Stack>

        <Stack spacing={2}>
          {Object.entries(reposByCategory).map(([category, categoryRepos]) => (
            <Box key={category}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  letterSpacing: 0.5,
                }}
              >
                {category}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {categoryRepos.map((repo) => (
                  <Box
                    key={repo.repoSlug}
                    onClick={() => handleToggleRepo(repo.repoSlug)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      bgcolor: selectedRepos.has(repo.repoSlug)
                        ? (theme) => alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                      border: '1px solid',
                      borderColor: selectedRepos.has(repo.repoSlug) ? 'primary.main' : 'divider',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    <Checkbox
                      checked={selectedRepos.has(repo.repoSlug)}
                      size="small"
                      sx={{ p: 0, mr: 1.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        {repo.repoName || repo.repoSlug}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        {repo.extractedAt 
                          ? `Last scanned: ${new Date(repo.extractedAt).toLocaleString()}`
                          : 'Not scanned yet'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={noneSelected || isLoading}
          startIcon={<ScanSearch size={16} />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isLoading ? 'Starting...' : `Scan ${selectedRepos.size} Repo${selectedRepos.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
